/**
 * MeetingPrepCard — presentational card for a single meeting prep (SLICE #7c-2, surface B).
 *
 * Pure layout: the parent (`MobileMeetingPrep`) owns the data fetch, the offline cache and the
 * primary/fallback selection. This component only renders what it is handed:
 *   • a header (title + when + attendee count, when known);
 *   • the agenda/brief markdown body via ReactMarkdown (same prose recipe as MyBriefs/MobileAsk);
 *   • an optional small "saved …" stale affordance.
 *
 * It is deliberately source-agnostic: a real meeting prebrief (research_prebriefs) and the
 * today's-prep fallback (research_role_prebriefs) both flatten down to {title, whenLabel,
 * attendeeCount?, markdown}.
 */
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Users } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface MeetingPrepCardProps {
  /** Headline for the card, e.g. the meeting title or "Today's prep". */
  title: string;
  /** Human-readable "when" line (absolute time for a meeting, or date for the fallback). */
  whenLabel?: string | null;
  /** Attendee count, when the source carries it (meeting prebriefs only). */
  attendeeCount?: number | null;
  /** A small kicker above the title, e.g. "Next meeting" or "Today". */
  kicker?: string | null;
  /** The agenda + brief markdown. */
  markdown?: string | null;
  /** Optional class passthrough. */
  className?: string;
}

export default function MeetingPrepCard({
  title,
  whenLabel,
  attendeeCount,
  kicker,
  markdown,
  className,
}: MeetingPrepCardProps) {
  const hasAttendees = typeof attendeeCount === 'number' && attendeeCount > 0;

  return (
    <article className={cn('rounded-xl border border-border bg-card p-4', className)}>
      {kicker && (
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">{kicker}</p>
      )}
      <h2 className="mt-0.5 font-heading text-base font-semibold text-foreground">{title}</h2>

      {(whenLabel || hasAttendees) && (
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          {whenLabel && <span>{whenLabel}</span>}
          {hasAttendees && (
            <span className="inline-flex items-center gap-1">
              <Users className="h-3.5 w-3.5" aria-hidden="true" />
              {attendeeCount} {attendeeCount === 1 ? 'attendee' : 'attendees'}
            </span>
          )}
        </div>
      )}

      {markdown ? (
        <div className="prose prose-sm mt-3 max-w-none font-body prose-headings:font-heading prose-a:text-primary">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
        </div>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">
          The agenda and brief for this meeting aren’t ready yet.
        </p>
      )}
    </article>
  );
}
